import type { FormControlProps } from '@mui/material';
import { createTheme, type Theme } from '@mui/material/styles';

export function createCustomTheme(mode: 'light' | 'dark'): Theme {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#E20074',
      },
      secondary: {
        main: '#D90000',
      },
      error: {
        main: '#E82010',
      },
    },
    breakpoints: {
      values: {
        xs: 444,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? 'white' : 'black',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? 'black' : 'white',
            marginBottom: '1rem',
            color: mode === 'dark' ? 'white' : 'black',
            border: `1px solid ${mode === 'dark' ? 'white' : 'black'}`,
            borderRadius: '15px',
            fontSize: '14px',
            padding: '10px',
          },
          select: {
            paddingTop: '14px',
            paddingBottom: '0px',
          },
          icon: {
            color: mode === 'dark' ? 'white' : 'black',
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            '& .MuiTableSortLabel-icon': {
              opacity: 1,
              color: mode === 'dark' ? '#444444' : '#D3D3D3',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderLeft: '4px solid transparent',
            borderBottom: '1px solid',
            borderBottomColor: mode === 'dark' ? '#464646' : '#D9D9D9',
            backgroundColor: 'transparent',
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 'bold',
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: 'small',
          fullWidth: true,
          margin: 'dense',
        },
        variants: [
          {
            props: { 'data-variant': 'padded' } as unknown as Partial<FormControlProps>,
            style: {
              paddingBottom: '1rem',
            },
          },
        ],
        styleOverrides: {
          root: {
            '& input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
            },
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: mode === 'dark' ? '#262626' : '#cfcfcf',
              cursor: 'not-allowed',
            },
            '& label.Mui-focused': {
              color: '#777',
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: '#777',
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#E0E3E7',
              },
              '&:hover fieldset': {
                borderColor: '#B2BAC2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#777',
              },
            },
            '& .MuiInputBase-input': {
              color: mode === 'dark' ? 'white' : 'black',
            },
            color: '#777',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: mode === 'dark' ? 'white' : 'black',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: '#777',
            '&.Mui-focused': {
              color: '#777',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? '#5c5c5c !important' : '#D0D0D2 !important',
              '&:hover': {
                backgroundColor: '#D0D0D2',
              },
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          square: true,
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            borderRadius: '.5rem',
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            color: '#E20074',
          },
          ol: {
            '& .MuiBreadcrumbs-separator': {
              color: '#E20074',
            },
            '& .MuiBreadcrumbs-li': {
              color: '#E20074',
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: '.5rem',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#262626' : '#F5F5F5',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '.3rem',
            fontFamily: 'TeleNeoWeb',
            fontSize: '1rem',
          },
          head: {
            fontSize: '0.8rem',
            fontWeight: 'bold',
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderBottom: 'none',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(221, 221, 221, 0.47)',
            zIndex: 1000,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            '&.MuiMenu-root.MuiModal-root': {
              zIndex: 7000,
            },
          },
          paper: {
            '&.MuiMenu-paper': {
              zIndex: 7000,
            },
          },
        },
      },
    },
    typography: {
      fontFamily: 'TeleNeoWeb',
    },
  });
}

// tempTheme uses the mocked createTheme in tests; in production it uses real MUI createTheme.
const tempTheme = createTheme();

/**
 * MUI breakpoint values in pixels for ODS components
 */
export const BREAKPOINTS = {
  xs: `${String(tempTheme.breakpoints.values.xs)}px`,
  sm: `${String(tempTheme.breakpoints.values.sm)}px`,
  md: `${String(tempTheme.breakpoints.values.md)}px`,
  lg: `${String(tempTheme.breakpoints.values.lg)}px`,
  xl: `${String(tempTheme.breakpoints.values.xl)}px`,
} as const;

/**
 * Breakpoint size names for prop validation
 */
export const BREAKPOINT_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

/**
 * Default breakpoint for modal/dialog components
 */
export const DEFAULT_MODAL_SIZE = 'sm' as const;
